import { StarIcon } from "@heroicons/react/24/outline";

import { Badge } from "@fronterahealth/frontera-ui-components/ui/badge";
import { Button } from "@fronterahealth/frontera-ui-components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@fronterahealth/frontera-ui-components/ui/dropdown-menu";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemMedia,
  ItemTitle,
} from "@fronterahealth/frontera-ui-components/ui/item";

import { ReportReviewerKind } from "@api/graphql/types-and-hooks";
import { getReviewUserDisplay } from "@components/QaReview/components/commentAuthor";
import { ReviewerAvatar } from "@components/ReportReview/ReviewerAvatar";
import { useCanManageReview } from "@components/ReportReview/hooks/useCanManageReview";
import { useCanReviewReport } from "@components/ReportReview/hooks/useCanReviewReport";
import { ApproveMenuItem } from "@components/ReportReview/menu/ApproveMenuItem";
import { FinalApproverMenuItem } from "@components/ReportReview/menu/FinalApproverMenuItem";
import { ReRequestMenuItem } from "@components/ReportReview/menu/ReRequestMenuItem";
import { RequestChangesMenuItem } from "@components/ReportReview/menu/RequestChangesMenuItem";
import { WithdrawDecisionMenuItem } from "@components/ReportReview/menu/WithdrawDecisionMenuItem";
import { WithdrawReviewerMenuItem } from "@components/ReportReview/menu/WithdrawReviewerMenuItem";
import {
  REVIEWER_STATUS_BADGE_CLASSES,
  REVIEWER_STATUS_LABELS,
  Reviewer,
  formatReviewTime,
  reviewerNotes,
} from "@components/ReportReview/reviewers";

export const ReviewerItem: React.FC<{ reviewer: Reviewer }> = ({ reviewer }) => {
  const lastEntry = reviewer.statusLog.at(-1);
  const notes = reviewerNotes(reviewer);
  const myReview = useCanReviewReport();
  const canManageReview = useCanManageReview();
  const showReviewGroup = myReview?.id === reviewer.id;
  const showManageGroup = canManageReview;

  return (
    <Item size="sm" className="px-0">
      <ItemMedia>
        <ReviewerAvatar reviewer={reviewer} />
      </ItemMedia>
      <ItemContent>
        <ItemTitle>
          <span className="truncate">{getReviewUserDisplay(reviewer.reviewer ?? null).name}</span>
          {reviewer.type === ReportReviewerKind.FinalApprover && (
            <span className="flex shrink-0 items-center gap-0.5 rounded bg-limestone-100 px-1 text-xs font-normal text-limestone-700">
              <StarIcon className="size-3" />
              Final approver
            </span>
          )}
        </ItemTitle>
        {lastEntry && (
          <ItemDescription>
            {REVIEWER_STATUS_LABELS[lastEntry.status]} {formatReviewTime(lastEntry.createdDate)}
          </ItemDescription>
        )}
        {notes.map((entry) => (
          <div key={entry.id} data-testid="reviewer-note" className="border-l-2 border-limestone-200 pl-2 text-xs">
            <p className="text-text-tertiary">
              {getReviewUserDisplay(entry.actor ?? null).name}, {REVIEWER_STATUS_LABELS[entry.status]}{" "}
              {formatReviewTime(entry.createdDate)}
            </p>
            <p className="whitespace-pre-wrap text-text-secondary">{entry.note.trim()}</p>
          </div>
        ))}
      </ItemContent>
      {reviewer.status && (
        <ItemActions>
          {showReviewGroup || showManageGroup ? (
            <DropdownMenu>
              <DropdownMenuTrigger
                render={
                  <Button variant="secondary" className={REVIEWER_STATUS_BADGE_CLASSES[reviewer.status]} size="sm">
                    {REVIEWER_STATUS_LABELS[reviewer.status]}
                  </Button>
                }
              />
              <DropdownMenuContent align="end" className="min-w-60">
                {showReviewGroup && (
                  <DropdownMenuGroup>
                    <DropdownMenuLabel>Review Report</DropdownMenuLabel>
                    <ApproveMenuItem />
                    <RequestChangesMenuItem />
                    <WithdrawDecisionMenuItem />
                  </DropdownMenuGroup>
                )}
                {showReviewGroup && showManageGroup && <DropdownMenuSeparator />}
                {showManageGroup && (
                  <DropdownMenuGroup>
                    <DropdownMenuLabel>Manage Reviewer</DropdownMenuLabel>
                    <FinalApproverMenuItem reviewer={reviewer} />
                    <ReRequestMenuItem reviewer={reviewer} />
                    <WithdrawReviewerMenuItem reviewer={reviewer} />
                  </DropdownMenuGroup>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Badge variant="secondary" className={REVIEWER_STATUS_BADGE_CLASSES[reviewer.status]}>
              {REVIEWER_STATUS_LABELS[reviewer.status]}
            </Badge>
          )}
        </ItemActions>
      )}
    </Item>
  );
};
